import { theme as defaultTheme } from "@mindlercare/ui-web";
import { Tab, Tabs } from "@mui/material";
import CONSTANTS from "core/constants";
import type { SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

export type TreatmentStatusTabValue =
  | typeof CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_START
  | typeof CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END
  | undefined;

type TreatmentStatusTabCountKey = NonNullable<TreatmentStatusTabValue> | "ALL";

export type TreatmentStatusTabCounts = Record<
  TreatmentStatusTabCountKey,
  number
>;

type TreatmentStatusTabsProps = {
  value: TreatmentStatusTabValue;
  onChange: (value: TreatmentStatusTabValue) => void;
  counts: TreatmentStatusTabCounts;
};

const TABS = [
  {
    labelKey: "TREATMENT_ACTIVE",
    value: CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_START,
    countKey: CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_START,
  },
  {
    labelKey: "TREATMENT_ENDED",
    value: CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END,
    countKey: CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END,
  },
  { labelKey: "ALL", value: undefined, countKey: "ALL" as const },
] as const satisfies readonly {
  labelKey: string;
  value: TreatmentStatusTabValue;
  countKey: TreatmentStatusTabCountKey;
}[];

const StyledTabs = styled(Tabs)`
  margin-top: ${defaultTheme.MARGIN.SM};
  margin-bottom: ${defaultTheme.MARGIN.MD};
`;

const StyledTab = styled(Tab)`
  &:hover {
    color: ${defaultTheme.COLORS.GRAY_BLACK};
    transition: all 200ms ease-in-out;
  }
`;

export const TreatmentStatusTabs = ({
  value,
  onChange,
  counts,
}: TreatmentStatusTabsProps) => {
  const { t } = useTranslation();

  const selectedIndex = TABS.findIndex((tab) => tab.value === value);
  const safeSelectedIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const handleTabChange = (_event: SyntheticEvent, index: number) => {
    onChange(TABS[index].value);
  };

  return (
    <StyledTabs
      value={safeSelectedIndex}
      onChange={handleTabChange}
      variant="scrollable"
      scrollButtons="auto"
    >
      {TABS.map((tab) => (
        <StyledTab
          key={tab.labelKey}
          label={`${t(tab.labelKey)} (${counts[tab.countKey] ?? 0})`}
          disableRipple
          data-testid={`treatment-status-tab-${tab.value ?? "ALL"}`}
        />
      ))}
    </StyledTabs>
  );
};
